import { useCallback, useMemo, useState } from 'react'
import { pickOutageJoke, type OutageJoke } from '../lib/outageJokes'
import FrankCard from './FrankCard'

interface OutageTakeoverProps {
  // Optional prop used by the /broken route to show the takeover
  // regardless of network state. Defaults to false, meaning the
  // component only renders when useOffline reports offline.
  forceShow?: boolean
  // Manually supplied joke for the /broken route so admins can preview
  // a specific one via query param (?j=3)
  pinnedJoke?: OutageJoke
  onDismiss?: () => void
}

// Full-viewport outage takeover. Designed to be screenshot-worthy and
// shareable — the whole point is that even when the site is "broken",
// the user has a moment that's worth posting. Rotates through the joke
// library (one new joke per session) via pickOutageJoke().
export default function OutageTakeover({
  forceShow = false,
  pinnedJoke,
  onDismiss,
}: OutageTakeoverProps) {
  // Pick a joke once at mount so the screen is stable while the user
  // reads it. A "give me another" button manually reshuffles.
  const [joke, setJoke] = useState<OutageJoke>(() => pinnedJoke ?? pickOutageJoke())

  const timestamp = useMemo(() => {
    const d = new Date()
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${hh}:${mm} · ${d.toDateString().toUpperCase()}`
  }, [])

  const handleAnother = useCallback(() => {
    setJoke(pickOutageJoke())
  }, [])

  const handleCopyLink = useCallback(async () => {
    try {
      const url = typeof window !== 'undefined' ? `${window.location.origin}/broken` : ''
      await navigator.clipboard.writeText(url)
    } catch {
      // Clipboard may be blocked; fail silently.
    }
  }, [])

  const handleRetry = useCallback(() => {
    if (onDismiss) {
      onDismiss()
      return
    }
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [onDismiss])

  if (!forceShow && !pinnedJoke) {
    // Real offline state detection happens in the parent via useOffline;
    // this component expects to be mounted only when show=true. But as a
    // safety, we never hard-return null here so a /broken route that
    // forgets forceShow still gets a visible screen.
  }

  return (
    <div className="fixed inset-0 z-[200] bg-bg-primary text-text-primary overflow-auto crt-scanlines">
      <div className="absolute inset-0 grid-backdrop pointer-events-none opacity-25" />

      {/* Pulsing orb backdrop */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(255,68,68,0.14) 0%, rgba(255,68,68,0.05) 35%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="relative z-10 min-h-full flex flex-col justify-between px-6 py-8 md:px-16 md:py-16">
        {/* Header mark */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-neon/10 border border-neon/40 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#39FF14"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
              </svg>
            </div>
            <div>
              <p className="brand-wordmark text-lg text-neon neon-text leading-none">
                FRANKENVIBES <span className="text-text-primary">LAB</span>
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted mt-1">
                {timestamp}
              </p>
            </div>
          </div>

          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-error animate-pulse">
            ● OFFLINE
          </span>
        </div>

        {/* Main copy */}
        <div className="max-w-3xl mx-auto text-center space-y-6 py-12">
          {/* Frank kneeling at the dead server rack — the operator
              himself looking sad about whatever just happened */}
          <FrankCard
            src="frank-server-offline.png"
            alt="Frank kneeling at the dead server rack"
            badge="● server · offline"
            badgeKind="error"
            glow="error"
            size="md"
            className="mx-auto"
          />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-neon/70">
            {joke.kicker}
          </p>
          <h1 className="brand-wordmark text-4xl md:text-6xl leading-[1.1] text-text-primary">
            {joke.headline}
          </h1>
          <p className="text-base md:text-lg text-text-secondary leading-relaxed max-w-2xl mx-auto">
            {joke.body}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
            <button
              onClick={handleRetry}
              className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-neon text-bg-primary hover:bg-neon-dim transition-colors cursor-pointer"
            >
              Try again
            </button>
            <button
              onClick={handleAnother}
              className="px-5 py-2.5 text-sm font-semibold rounded-lg border border-border hover:border-neon hover:text-neon text-text-secondary transition-colors cursor-pointer"
            >
              Give me another
            </button>
            <button
              onClick={handleCopyLink}
              className="px-5 py-2.5 text-sm font-semibold rounded-lg border border-border hover:border-neon hover:text-neon text-text-secondary transition-colors cursor-pointer"
            >
              Copy share link
            </button>
          </div>
        </div>

        {/* Disclaimer footer */}
        <div className="max-w-2xl mx-auto text-center space-y-2 pt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
            / THE FINE PRINT
          </p>
          <p className="text-[11px] text-text-muted leading-relaxed italic">
            None of the above is confirmed, endorsed, or particularly well-researched.
            It may or may not reference actual persons, infrastructure, cables,
            geopolitical events, motel incidents, weather, or mollusks. We are, as
            previously stated, currently unplugged from the internet and therefore
            in no position to fact-check ourselves. Share responsibly.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted pt-2">
            frankenvibes-lab.pages.dev/broken
          </p>
        </div>
      </div>
    </div>
  )
}
